import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { useFetch } from '../../../hooks/useFetch';
import { usePagination } from '../../../hooks/usePagination';
import { PageableResponseWrapper, PageQueryParams } from '../../../domain/response-wrapper';
import { CustomerSettlement } from '../models/customer-settlement';
import { getLastDaysDateRange, LAST_DAYS_DEFAULT_RANGE } from '../../home/util/GetDateRangeUtil';
import {
  API_DATE_PATTERN,
  SETTLEMENTS_ENDPOINTS,
  SETTLEMENTS_PAGE_SIZE,
  SETTLEMENTS_REQUEST_TIMEOUT_MS,
} from '../settlements.constants';

export interface SettlementFiltersQueryParams extends PageQueryParams {
  start: string;
  end: string;
}

export type SettlementReportParams = Pick<SettlementFiltersQueryParams, 'start' | 'end'>;

export const buildDefaultSettlementFilters = (today: Date = new Date()): SettlementFiltersQueryParams => {
  const { startDate, endDate } = getLastDaysDateRange(LAST_DAYS_DEFAULT_RANGE, today);
  return {
    page: 0,
    size: SETTLEMENTS_PAGE_SIZE,
    start: format(startDate, API_DATE_PATTERN),
    end: format(endDate, API_DATE_PATTERN),
  };
};

export const useSettlementFilters = () => {
  const [defaultFilters] = useState(() => buildDefaultSettlementFilters());
  const { queryParams, nextPage, prevPage, updateParams } = usePagination<SettlementFiltersQueryParams>(defaultFilters);
  const { start, end, page, size } = queryParams;

  // Los valores de la URL llegan como string: se normalizan y solo se envían los parámetros del contrato.
  const requestParams = useMemo<SettlementFiltersQueryParams>(() => ({
    start,
    end,
    page: Number(page),
    size: Number(size),
  }), [start, end, page, size]);

  const reportParams = useMemo<SettlementReportParams>(() => ({ start, end }), [start, end]);

  const { data, loading, error } = useFetch<PageableResponseWrapper<CustomerSettlement>>(
    SETTLEMENTS_ENDPOINTS.summary,
    requestParams,
    true,
    SETTLEMENTS_REQUEST_TIMEOUT_MS,
  );

  const setDateRange = (newStart: string, newEnd: string) => {
    updateParams({ start: newStart, end: newEnd, page: 0 });
  };

  return {
    queryParams: requestParams,
    reportParams,
    results: data,
    error,
    loading,
    nextPage,
    prevPage,
    setDateRange,
  };
};
